// What a new client cannot be saved without, and what each field has to look
// like once it holds anything. The rules shared with the brand form — phone,
// email, name casing — come from `lib/fieldFormat`; what is here is the rest.
//
// The red asterisk each label carries is drawn from this list — so a field
// moved between the lists below changes what the form says as well as what it
// enforces.
//
// What has to be filled in is who the client is, what they are working
// towards, and how to reach them. A title, a date of birth, a profession, the
// state and PIN half of an address, a food preference: all worth having, none
// of them worth refusing to save a client over. The practitioner in front of
// this form usually has the person on the phone and can fill the rest in
// afterwards — and a form that will not save is how the rest never gets
// entered at all.
//
// Three of those cost something on the wire rather than here, and it is paid
// in `clientPayload`: a title and a food preference go up as catalog ids and a
// birthday as an ISO date, so none of the three can travel as the empty string
// the form holds. Their keys are left off the body instead — see
// `toCreateClientPayload`.

import {
  digitsOf,
  emailError,
  mobileError,
  pincodeError,
} from "../../lib/fieldFormat";
import { formatPrice } from "../brand/planFormat";
import { NewClient } from "./types/clients.types";

export const REQUIRED_FIELDS = [
  "firstName",
  "lastName",
  "gender",
  "lifestyle",
  "city",
  "phone",
  "whatsapp",
  "email",
] as const;

/**
 * Asked for, checked once answered, and never a reason the form will not save.
 *
 * Four of these carry no rule of their own and are here only so the form can
 * still address them: `ValidatedField` is what `controlId` and `errorFor` are
 * keyed on, so a field in none of these lists is one the form cannot point at.
 * `fieldError` has no case for them, which is the same as saying anything they
 * can hold is fine.
 *
 * The other two do have rules, and that is the point of keeping the two kinds
 * together here: a blank DOB saves, and 31/02/1990 still does not.
 */
export const OPTIONAL_VALIDATED_FIELDS = [
  "title",
  "dob",
  "profession",
  "state",
  "pincode",
  "foodPreference",
] as const;

/**
 * Fields holding a list rather than a line of text, and which cannot be left
 * empty. A client is signed up to achieve something, so at least one goal has
 * to be picked — several may be, which is what puts the field here rather than
 * among the single answers above.
 *
 * Kept apart from `REQUIRED_FIELDS` because the checks there are all string
 * rules, and an array has nothing to trim.
 */
export const REQUIRED_LIST_FIELDS = ["goals"] as const;

/**
 * Fields holding a list which may be empty — a client with nothing on record
 * reacts to nothing. The names still belong to `ValidatedField` so each control
 * keeps its id and its touched flag.
 */
export const LIST_FIELDS = ["foodAllergies"] as const;

/**
 * The subscription the client arrives already holding. Optional as a group and
 * required within it: the three describe one purchase, and two of them is not
 * a purchase anybody can be billed for. Checked by `subscriptionErrors` rather
 * than by `fieldError`, which has no way to see the other two.
 */
export const SUBSCRIPTION_FIELDS = [
  "planId",
  "paidPrice",
  "purchasedDate",
] as const;

export type RequiredField = (typeof REQUIRED_FIELDS)[number];
export type OptionalValidatedField = (typeof OPTIONAL_VALIDATED_FIELDS)[number];
export type RequiredListField = (typeof REQUIRED_LIST_FIELDS)[number];
export type ListField = (typeof LIST_FIELDS)[number];
export type SubscriptionField = (typeof SUBSCRIPTION_FIELDS)[number];
export type ValidatedField =
  | RequiredField
  | OptionalValidatedField
  | RequiredListField
  | ListField
  | SubscriptionField;

/** The text fields, which is what `fieldError` below reads. */
export const VALIDATED_FIELDS = [
  ...REQUIRED_FIELDS,
  ...OPTIONAL_VALIDATED_FIELDS,
] as const;

export type NewClientErrors = Partial<Record<ValidatedField, string>>;

/** Names used in "<field> is required", so the message reads like the label. */
const LABELS: Record<ValidatedField, string> = {
  title: "Title",
  firstName: "First name",
  lastName: "Last name",
  gender: "Gender",
  dob: "DOB",
  profession: "Profession",
  lifestyle: "Life style",
  state: "State",
  city: "City",
  pincode: "Pincode",
  phone: "Phone number",
  whatsapp: "WhatsApp number",
  email: "Email address",
  goals: "Goal",
  foodPreference: "Food preference",
  foodAllergies: "Food allergies",
  planId: "Plan",
  paidPrice: "Paid price",
  purchasedDate: "Purchased date",
};

/** A person's name: letters, plus the joiners that appear inside one. */
const NAME_PATTERN = /^[A-Za-z][A-Za-z\s.'-]*[A-Za-z.]$|^[A-Za-z]$/;

const DOB_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** Nobody on a nutrition plan was born more than this long ago. */
export const MAX_AGE_YEARS = 120;

/**
 * A typed date read against the calendar rather than just the pattern: 31/02
 * matches the shape perfectly and is still not a day. Either the date itself,
 * or what is wrong with the text — the two are returned together because every
 * caller needs both, and parsing twice is how they drift apart.
 */
const readDate = (value: string): { date?: Date; error: string } => {
  const match = DOB_PATTERN.exec(value);
  if (!match) return { error: "Enter the date as DD/MM/YYYY" };

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  // `new Date(2026, 1, 31)` rolls forward to 3 March rather than failing, so
  // reading the parts back off the date is what catches a day that does not
  // exist in its month.
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return { error: "That date does not exist" };
  }

  return { date, error: "" };
};

/** A date of birth: a real day, behind us, and within a lifetime. */
const dobError = (value: string): string => {
  const { date, error } = readDate(value);
  if (!date) return error;

  const today = new Date();
  if (date > today) return "Date of birth cannot be in the future";
  if (date.getFullYear() < today.getFullYear() - MAX_AGE_YEARS)
    return "Enter a valid date of birth";

  return "";
};

/**
 * The day a subscription was bought. Money that has already changed hands
 * cannot have changed hands tomorrow — a future date here is a typo, and one
 * that would date the client's engagement from it.
 */
const purchasedDateError = (value: string): string => {
  const { date, error } = readDate(value);
  if (!date) return error;

  // Compared against the end of today, so a purchase made this morning is not
  // read as being in the future.
  const now = new Date();
  const endOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59
  );
  if (date > endOfToday) return "Purchased date cannot be in the future";

  return "";
};

const isRequired = (field: ValidatedField): field is RequiredField =>
  (REQUIRED_FIELDS as readonly string[]).includes(field);

/** Empty string means the field is fine. */
export const fieldError = (field: ValidatedField, raw: string): string => {
  const value = raw.trim();

  // An empty optional field has nothing to be wrong about.
  if (!value) return isRequired(field) ? `${LABELS[field]} is required` : "";

  switch (field) {
    case "firstName":
    case "lastName":
      return NAME_PATTERN.test(value)
        ? ""
        : `${LABELS[field]} can only contain letters`;
    case "dob":
      return dobError(value);
    case "pincode":
      return pincodeError(value);
    case "phone":
    case "whatsapp":
      return mobileError(LABELS[field], value);
    case "email":
      return emailError(value);
    // The dropdown fields can only ever hold an option they were given, and
    // the free-text allergy line takes whatever it is told.
    default:
      return "";
  }
};

/**
 * The list fields that have to hold something. An empty one reads as the same
 * "<field> is required" a blank dropdown does, so the form says the same thing
 * about a goal whether it takes one answer or several.
 */
export const listFieldError = (
  field: RequiredListField,
  values: string[]
): string => (values.length === 0 ? `${LABELS[field]} is required` : "");

/**
 * The subscription group, which is only checked once it has been started —
 * every field left alone is a client added without one, and that is the usual
 * case rather than an omission.
 *
 * `planPrice` is the list price of the plan picked, where one has been:
 * the paid price may be under it, which is a discount, but never over it —
 * that is a rise, and nothing on this form is for raising a price.
 */
export const subscriptionErrors = (
  client: NewClient,
  planPrice?: number
): NewClientErrors => {
  const planId = client.planId.trim();
  const paidPrice = client.paidPrice.trim();
  const purchasedDate = client.purchasedDate.trim();

  // Untouched, so there is no subscription to be wrong about.
  if (!planId && !paidPrice && !purchasedDate) return {};

  const errors: NewClientErrors = {};

  if (!planId) errors.planId = "Plan is required";

  if (!paidPrice) {
    errors.paidPrice = "Paid price is required";
  } else {
    const amount = Number(paidPrice);
    if (!Number.isFinite(amount) || amount <= 0) {
      errors.paidPrice = "Enter the amount that was paid";
    } else if (planPrice !== undefined && amount > planPrice) {
      errors.paidPrice = `That is more than the plan price of ${formatPrice(planPrice)}`;
    }
  }

  if (!purchasedDate) {
    errors.purchasedDate = "Purchased date is required";
  } else {
    const message = purchasedDateError(purchasedDate);
    if (message) errors.purchasedDate = message;
  }

  return errors;
};

/**
 * Only failing fields appear, so an empty object means the form is valid.
 *
 * `planPrice` belongs to the subscription group — see `subscriptionErrors`.
 * The edit screen leaves it off, and asks for no subscription at all.
 */
export const validateNewClient = (
  client: NewClient,
  planPrice?: number
): NewClientErrors => {
  const errors: NewClientErrors = {};

  VALIDATED_FIELDS.forEach((field) => {
    const message = fieldError(field, client[field]);
    if (message) errors[field] = message;
  });

  REQUIRED_LIST_FIELDS.forEach((field) => {
    const message = listFieldError(field, client[field]);
    if (message) errors[field] = message;
  });

  // Nothing to check for `LIST_FIELDS`: an empty one is a client with no
  // allergies, which is a perfectly good client.
  return { ...errors, ...subscriptionErrors(client, planPrice) };
};

/* ── Input normalisers ─────────────────────────────────────────────────── */

/**
 * Types the slashes for you: only digits are kept, and a separator appears as
 * each part fills up. Deleting one deletes the digit before it, which is what
 * makes backspacing through the field behave.
 *
 * The design's placeholder reads DD/MM/YY, but a two-digit year cannot say
 * whether 98 means 1998 or 2098 — for a date of birth that is the whole
 * point of the field, so the year is taken in full.
 */
export const normaliseDobInput = (raw: string): string => {
  const digits = digitsOf(raw).slice(0, 8);

  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
    .filter(Boolean)
    .join("/");
};

/**
 * Whole rupees, digits only — the ₹ is painted inside the field and the app
 * prices plans in whole rupees throughout, so a decimal point typed here
 * would only ever be a slip.
 */
export const normaliseAmountInput = (raw: string): string =>
  digitsOf(raw).slice(0, 9);

/** Letters and the joiners inside a name; anything else never lands. */
export const normaliseNameInput = (raw: string): string =>
  raw.replace(/[^A-Za-z\s.'-]/g, "");

